import React, { useEffect, useMemo, useState } from "react";
import { Tabs, Tab, TabProps, TabsProps } from "@mui/material";
import { useHistory, useLocation, useParams } from "react-router-dom";

export interface TabConfigItem extends TabProps {
    key: string;
    title: string;
    content?: React.ReactNode;
    props?: TabProps;
}

interface BaseTabsProps extends Omit<TabsProps, "onChange" | "value"> {
    tabs: TabConfigItem[];
    value?: string; // The key of the active tab
    onChange?: (newKey: string) => void;
}

export default function BaseTabs({ tabs, value, onChange, ...props }: BaseTabsProps) {
    const isControlled = value !== undefined;
    const location = useLocation();
    const history = useHistory();
    const { page } = useParams<{ page: string }>();

    // Check if the current path matches any of the tab keys
    const indexToKey = useMemo(() =>
        Object.fromEntries(tabs.map((tab, i) => [i, tab.key])), [tabs]);

    // Create a mapping of tab keys to their indices
    const keyToIndex = useMemo(() =>
        Object.fromEntries(tabs.map((tab, i) => [tab.key, i])), [tabs]);

    const [uncontrolledSelectedTab, setUncontrolledSelectedTab] = useState<number>(() => {
        return keyToIndex[page] ?? 0;
    });

    const selectedTab = isControlled ? (keyToIndex[value] ?? 0) : uncontrolledSelectedTab;

    const handleChange = (_: React.SyntheticEvent, newValue: number) => {
        const newKey = indexToKey[newValue];

        if (onChange) {
            onChange(newKey);
        }

        if (!isControlled) {
            const currentTabKeys = tabs.map(tab => tab.key);
            const parts = location.pathname.split("/").filter(Boolean);

            const tabIndex = parts.findIndex(p => currentTabKeys.includes(p));

            if (tabIndex === -1) {
                const newPath = [...parts, newKey].join("/");
                history.push("/" + newPath);
                return;
            };

            // Replace tab key and strip everything after it
            const newPathParts = [...parts.slice(0, tabIndex), newKey];
            const newPath = "/" + newPathParts.join("/");

            history.push(newPath);
        }
    };

    // Automatically redirect to the first tab if no tab key is present in the URL
    useEffect(() => {
        if (isControlled) return;

        const currentTabKeys = tabs.map(tab => tab.key);
        const parts = location.pathname.split("/").filter(Boolean);

        const tabIndex = parts.findIndex(p => currentTabKeys.includes(p));

        // If no tab key is present, redirect to default tab
        if (tabIndex === -1 && tabs[0]?.key) {
            const newPath = [...parts, tabs[0].key].join("/");
            history.replace("/" + newPath);
        }
    }, [location.pathname, tabs, isControlled]);

    // Update selectedTab when the page changes (for navigation via browser back/forward buttons)
    useEffect(() => {
        if (isControlled) return;
        setUncontrolledSelectedTab(keyToIndex[page] ?? 0);
    }, [page, keyToIndex, isControlled]);

    useEffect(() => {
        if (!isControlled || !value || !tabs.some(tab => tab.key === value)) {
            return;
        }

        const currentTabKeys = tabs.map(tab => tab.key);
        const parts = location.pathname.split("/").filter(Boolean);
        const tabIndex = parts.findIndex(p => currentTabKeys.includes(p));

        const newUrlRequired = tabIndex === -1 || parts[tabIndex] !== value;

        if (newUrlRequired) {
            let newPathParts;
            if (tabIndex !== -1) {
                // Replace existing tab in URL and remove everything after it
                newPathParts = [...parts.slice(0, tabIndex), value];
            } else {
                // Append new tab to URL if no tab was found.
                // This assumes the tab key should be appended at the end of the current path.
                newPathParts = [...parts, value];
            }
            const newPath = "/" + newPathParts.join("/");
            history.push(newPath);
        }
    }, [value, isControlled, location.pathname, history, tabs]);

    return (
        <>
            <Tabs
                {...props}
                
                value={selectedTab}
                onChange={handleChange}
                className="mb-10 bg-stepper-main rounded-[100px]"
                variant="fullWidth"
                selectionFollowsFocus
                TabIndicatorProps={{
                    style: {
                        backgroundColor: "#7556fa",
                        borderRadius: "25px",
                        height: "100%",
                    },
                }}
                sx={{
                    "& .Mui-selected": {
                        color: "#fff !important",
                        zIndex: 1,
                        outline: "none",
                    },
                    "& .MuiTab-root": {
                        color: "#686868",
                        fontSize: "16px",
                        fontWeight: 400,
                        padding: "16px 0",
                        textTransform: "none",
                        "&:hover": {
                            color: "#7556fa",
                            backgroundColor: "transparent",
                        },
                        "&.Mui-selected": {
                            color: "#fff",
                            backgroundColor: "transparent",
                        },
                    },
                }}
            >
                {tabs.map(tab => {
                  if (tab.hidden) return null; // Skip rendering this tab if it's hidden

                  return (
                    <Tab
                      key={tab.key}
                      label={tab.title}
                      disabled={tab.disabled}
                      disableRipple
                      {...tab.props}
                    />
                  );
                })}
            </Tabs>
            <div>{tabs[selectedTab]?.content}</div>
        </>
    );
}